import { useColorScheme } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useAppStateProperty } from '@inform-appshell/fusion-kit-react';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'cuam-theme';

function getInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * The shell's colour mode.
 *
 * The mode lives in `ApplicationStateService`, which the shell owns and injects into every MFE, so
 * the signal already crosses the federation boundary. `setMode` flips `data-mui-color-scheme` on
 * `<html>` and swaps the GB30 palette; MUI persists the mode under `modeStorageKey`, so this hook
 * no longer writes the key itself.
 *
 * Identical to the webapp's `hooks/useTheme.ts` on purpose: shell and MFE are separately bundled
 * with no shared local package, so sharing one hook would mean a new workspace package for it.
 */
export function useTheme() {
  const [theme, setTheme] = useAppStateProperty<Theme>('theme', getInitialTheme());
  const { setMode } = useColorScheme();

  useEffect(() => {
    setMode(theme);
  }, [theme, setMode]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return { theme, isDark: theme === 'dark', toggleTheme } as const;
}
